import { useState, useEffect } from "react";
import { useTheme } from "../hooks/useTheme";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { Bars3Icon, XMarkIcon, ChevronUpDownIcon, CheckIcon } from "@heroicons/react/24/outline";
import { UserCircleIcon } from "@heroicons/react/20/solid";
import logo from "../assets/logo.svg";
import useSessionStore from "../stores/sessionStore";
import authProvider from "../authProvider";
import usePlan from "../hooks/usePlan";

const userNavigation = [
  { name: "Settings", href: "/settings" },
  { name: "Your Profile", href: "/profile" },
];

const planLabel = {
  hobby: "Hobby",
  pro: "Pro",
  enterprise: "Enterprise",
};

const planBadgeClass = {
  hobby: "bg-gray-100 text-gray-600",
  pro: "bg-emerald-100 text-emerald-700",
  enterprise: "bg-purple-100 text-purple-700",
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function AppMenu() {
  const [theme, toggleTheme] = useTheme();
  const user = useSessionStore((state) => state.user);
  const setUser = useSessionStore((state) => state.setUser);
  const { plan, teams, planLoading } = usePlan();
  const queryClient = useQueryClient();

  const [switching, setSwitching] = useState(false);
  const [toast, setToast] = useState(null); // { message, isError }

  const signout = async () => {
    await authProvider.logout();
  };

  const showToast = (message, isError = false) => {
    setToast({ message, isError });
    setTimeout(() => setToast(null), 3000);
  };

  const handleTeamSwitch = async (teamId) => {
    if (teamId === user?.profile?.team_id || switching) return;
    setSwitching(true);
    try {
      const API = import.meta.env.VITE_API_BASE_URL;
      const raw = localStorage.getItem("fungistics-profile");
      const csrfToken = raw ? (JSON.parse(raw)._csrf ?? "") : "";
      const res = await fetch(`${API}teams/switch`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify({ team_id: teamId }),
      });
      if (!res.ok) throw new Error("switch failed");
      const data = await res.json();
      const newCsrf = res.headers.get("X-CSRF-Token") ?? "";
      const updatedProfile = { ...data, _csrf: newCsrf };

      // Clear all cached query data so we don't show stale data from the old team (H7)
      queryClient.clear();

      // Update the session store and localStorage with new profile + fresh CSRF token
      setUser({ ...user, profile: updatedProfile });
      localStorage.setItem("fungistics-profile", JSON.stringify(updatedProfile));

      showToast(`Now viewing: ${updatedProfile.team_name}`);
      window.location.href = "/";
    } catch {
      showToast("Failed to switch teams. Please try again.", true);
    } finally {
      setSwitching(false);
    }
  };

  useEffect(() => {
    const currentPath = window.location.pathname;
    const updatedMenu = menu.map((item) => ({
      ...item,
      current: item.href === currentPath,
    }));
    setMenu(updatedMenu);
  }, []);

  const navigation = [{ name: "Dashboard", href: "/", current: true }];
  navigation.push({ name: "Grows", href: "/grows", current: false });
  navigation.push({ name: "Inventory", href: "/inventory", current: false });
  navigation.push({ name: "Recipes", href: "/recipes", current: false });
  navigation.push({ name: "Analytics", href: "/analytics", current: false });
  navigation.push({ name: "Glossary", href: "/glossary", current: false });
  navigation.push({ name: "Support", href: "/support", current: false });

  const [menu, setMenu] = useState(navigation);

  const currentTeamName = user?.profile?.team_name ?? "";
  const currentTeamId = user?.profile?.team_id;
  const multiTeam = !planLoading && teams.length > 1;

  return (
    <>
      {/* Toast notification */}
      {toast && (
        <div
          className={classNames(
            "fixed top-4 right-4 z-50 rounded-md px-4 py-2 text-sm font-medium shadow-lg",
            toast.isError
              ? "bg-red-600 text-white"
              : "bg-cyan-700 text-white"
          )}
        >
          {toast.message}
        </div>
      )}

      <Disclosure
        as="nav"
        className="border-b border-cyan-300/25 bg-cyan-800 lg:border-none"
      >
        <div className="mx-auto max-w-7xl px-2 sm:px-4 lg:px-8">
          <div className="relative flex h-16 items-center justify-between lg:border-b lg:border-cyan-400/25">
            <div className="flex items-center px-2 lg:px-0">
              {/* Logo */}
              <div className="shrink-0">
                <img
                  alt="Fungistics Logo"
                  src={logo}
                  className="block size-8"
                />
              </div>

              {/* Team name / switcher — always shown, dropdown only when multi-team */}
              <div className="ml-3 flex items-center">
                {multiTeam ? (
                  <Menu as="div" className="relative">
                    <MenuButton
                      disabled={switching}
                      className="flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-cyan-100 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-cyan-800 disabled:opacity-60"
                    >
                      <span>{currentTeamName}</span>
                      {plan && (
                        <span className={classNames("rounded px-1.5 py-0.5 text-xs font-semibold", planBadgeClass[plan] ?? planBadgeClass.hobby)}>
                          {planLabel[plan] ?? plan}
                        </span>
                      )}
                      <ChevronUpDownIcon className="size-4 text-cyan-300" aria-hidden="true" />
                    </MenuButton>
                    <MenuItems className="absolute left-0 z-20 mt-2 w-56 origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                      {teams.map((team) => (
                        <MenuItem key={team.id}>
                          <button
                            onClick={() => handleTeamSwitch(team.id)}
                            className="flex w-full items-center justify-between px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100"
                          >
                            <span className="flex items-center gap-2">
                              {team.id === currentTeamId && (
                                <CheckIcon className="size-4 text-cyan-600" aria-hidden="true" />
                              )}
                              {team.id !== currentTeamId && <span className="size-4" />}
                              {team.name}
                            </span>
                            <span className={classNames("rounded px-1.5 py-0.5 text-xs font-semibold", planBadgeClass[team.plan] ?? planBadgeClass.hobby)}>
                              {planLabel[team.plan] ?? team.plan}
                            </span>
                          </button>
                        </MenuItem>
                      ))}
                    </MenuItems>
                  </Menu>
                ) : (
                  // Single team: static display
                  <span className="flex items-center gap-1.5 text-sm font-medium text-cyan-100">
                    {currentTeamName}
                    {!planLoading && plan && (
                      <span className={classNames("rounded px-1.5 py-0.5 text-xs font-semibold", planBadgeClass[plan] ?? planBadgeClass.hobby)}>
                        {planLabel[plan] ?? plan}
                      </span>
                    )}
                  </span>
                )}
              </div>

              {/* Desktop nav links */}
              <div className="hidden lg:ml-6 lg:block">
                <div className="flex space-x-4">
                  {menu.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      aria-current={item.current ? "page" : undefined}
                      className={classNames(
                        item.current
                          ? "bg-cyan-700 text-white"
                          : "text-white hover:bg-cyan-500/75",
                        "rounded-md px-3 py-2 text-sm font-medium"
                      )}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex lg:hidden">
              {/* Mobile menu button */}
              <DisclosureButton className="group relative inline-flex items-center justify-center rounded-md bg-cyan-800 p-2 text-cyan-200 hover:bg-cyan-500/75 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-cyan-800">
                <span className="absolute -inset-0.5" />
                <span className="sr-only">Open main menu</span>
                <Bars3Icon
                  aria-hidden="true"
                  className="block size-6 group-data-[open]:hidden"
                />
                <XMarkIcon
                  aria-hidden="true"
                  className="hidden size-6 group-data-[open]:block"
                />
              </DisclosureButton>
            </div>

            <div className="hidden lg:ml-4 lg:block">
              <div className="flex items-center">
                {/* Theme toggle */}
                <button
                  onClick={toggleTheme}
                  className="relative shrink-0 rounded-full bg-cyan-800 p-1 text-cyan-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-cyan-800"
                  aria-label="Toggle theme"
                >
                  <span className="absolute -inset-1.5" />
                  {theme === "dark" ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" />
                    </svg>
                  )}
                </button>

                {/* Profile dropdown */}
                <Menu as="div" className="relative ml-3 shrink-0">
                  <div>
                    <MenuButton className="relative flex rounded-full bg-cyan-800 text-sm text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-cyan-800">
                      <span className="absolute -inset-1.5" />
                      <span className="sr-only">Open user menu</span>
                      <UserCircleIcon
                        aria-hidden="true"
                        className="size-8 rounded-full"
                      />
                    </MenuButton>
                  </div>
                  <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    {userNavigation.map((item) => (
                      <MenuItem key={item.name}>
                        <Link
                          to={item.href}
                          className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                        >
                          {item.name}
                        </Link>
                      </MenuItem>
                    ))}
                    <MenuItem>
                      <div
                        onClick={signout}
                        className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:outline-none cursor-pointer"
                      >
                        Sign out
                      </div>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              </div>
            </div>
          </div>
        </div>

        <DisclosurePanel className="lg:hidden">
          <div className="space-y-1 px-2 pb-3 pt-2">
            {navigation.map((item) => (
              <DisclosureButton
                key={item.name}
                as={Link}
                to={item.href}
                aria-current={item.current ? "page" : undefined}
                className={classNames(
                  item.current
                    ? "bg-cyan-700 text-white"
                    : "text-white hover:bg-cyan-500/75",
                  "block rounded-md px-3 py-2 text-base font-medium"
                )}
              >
                {item.name}
              </DisclosureButton>
            ))}
          </div>
          <div className="border-t border-cyan-700 pb-3 pt-4">
            <div className="flex items-center px-5">
              <div className="shrink-0">
                <UserCircleIcon
                  aria-hidden="true"
                  className="size-10 rounded-full text-cyan-200"
                />
              </div>
              <div className="ml-3">
                <div className="text-sm font-medium text-cyan-300">
                  {user?.email}
                </div>
                <div className="text-xs text-cyan-400">
                  {currentTeamName}
                </div>
              </div>
            </div>
            <div className="mt-3 space-y-1 px-2">
              {userNavigation.map((item) => (
                <DisclosureButton
                  key={item.name}
                  as={Link}
                  to={item.href}
                  className="block rounded-md px-3 py-2 text-base font-medium text-white hover:bg-cyan-500/75"
                >
                  {item.name}
                </DisclosureButton>
              ))}

              <DisclosureButton
                onClick={signout}
                className="block rounded-md px-3 py-2 text-base font-medium text-white hover:bg-cyan-500/75"
              >
                Sign out
              </DisclosureButton>
            </div>
          </div>
        </DisclosurePanel>
      </Disclosure>
    </>
  );
}
